import { lightPose } from './lightingData.js';
import { connectedStairs, stairSpec, stairWorld, stairFootprint } from './stairs.js';
import { X, Y } from './render.js';
import { explainPoint } from './explain.js';
import { installationEstimate } from './installation.js';
import { taskDemands } from './tasks.js';
import { openingCentre } from './siteGeometry.js';
import { personParts, presentInScene } from './sceneObjects.js';
import { isPoleObject, poleDiameter } from './poles.js';

export function drawDesign(ctx, S, view, { probe, selected, draft, hover, labelMode = 'auto' }) {
  const point = p => [X(view, p.x), Y(view, p.y)];
  const occupied = [];
  const label = (text, x, y, type, id) => {
    const on = selected?.type === type && selected.id === id;
    if (!on && (['selected', 'essential'].includes(labelMode) || (labelMode === 'auto' && (view.k < 18 || (type === 'light' && view.k < 35))))) return;
    const rect = { x, y: y - 12, w: ctx.measureText(text).width + 8, h: 16 };
    if (!on && labelMode !== 'all' && occupied.some(r => rect.x < r.x + r.w && rect.x + rect.w > r.x && rect.y < r.y + r.h && rect.y + rect.h > r.y)) return;
    occupied.push(rect); ctx.fillText(text, x, y);
  };
  ctx.save(); ctx.font = '11px sans-serif';
  for (const wall of S.walls) {
    if (!wall.mountRule || wall.mountRule === 'allowed') continue;
    ctx.strokeStyle = wall.mountRule === 'preferred' ? '#139b76' : '#d65a53'; ctx.lineWidth = 3; ctx.setLineDash([5, 4]);
    ctx.beginPath(); ctx.moveTo(...point({ x: wall.x1, y: wall.y1 })); ctx.lineTo(...point({ x: wall.x2, y: wall.y2 })); ctx.stroke();
  }
  ctx.setLineDash([]);
  for (const s of connectedStairs(S)) {
    const c = stairSpec(S, s), on = selected?.type === 'stair' && selected.id === s.id, upper = S.activeFloorId === s.toFloorId;
    ctx.save(); ctx.strokeStyle = c ? (on ? '#2563eb' : '#596e7f') : '#d65a53'; ctx.fillStyle = on ? '#dbeafeed' : '#edf1f4e8'; ctx.lineWidth = on ? 2 : 1;
    ctx.beginPath(); stairFootprint(s).forEach((p, i) => i ? ctx.lineTo(...point(p)) : ctx.moveTo(...point(p))); ctx.closePath(); ctx.fill(); ctx.stroke();
    const line = (a, b) => { ctx.beginPath(); ctx.moveTo(...point(a)); ctx.lineTo(...point(b)); ctx.stroke(); };
    const count = c?.count || 12;
    for (let i = 0; i <= count; i++) { const u = -s.run / 2 + i * s.run / count; line(stairWorld(s, u, -s.width / 2), stairWorld(s, u, s.width / 2)); }
    const sign = upper ? -1 : 1, tip = sign * s.run * 0.35, a = stairWorld(s, tip), b = stairWorld(s, -tip);
    ctx.lineWidth = 2; line(b, a); line(a, stairWorld(s, tip - sign * 0.25, -0.18)); line(a, stairWorld(s, tip - sign * 0.25, 0.18));
    const [x, y] = point(stairWorld(s, 0, -s.width / 2 - 0.15));
    const stairLabel = `${s.name} · ${upper ? 'DOWN' : 'UP'}${c ? ' to ' + (upper ? c.lower.name : c.upper.name) : ' · Check levels'}`;
    ctx.font = '11px sans-serif';
    ctx.fillStyle = '#334155'; label(stairLabel, x, y, 'stair', s.id); ctx.restore();
  }
  for (const opening of S.openings || []) {
    const c = openingCentre(S, opening); if (!c) continue;
    ctx.fillStyle = '#6a44b3'; label(opening.name, ...point({ x: c.x + 0.1, y: c.y - 0.15 }), 'opening', opening.id);
  }
  for (const d of taskDemands(S)) {
    ctx.strokeStyle = '#8c4ccc'; ctx.lineWidth = 2; ctx.setLineDash([5, 3]);
    ctx.beginPath(); d.points.forEach((p, i) => i ? ctx.lineTo(...point(p)) : ctx.moveTo(...point(p))); ctx.stroke();
    const p = d.points.at(-1), a = d.heading * Math.PI / 180, [x, y] = point(p);
    ctx.setLineDash([]); ctx.beginPath(); ctx.moveTo(x - 8 * Math.cos(a - 0.5), y - 8 * Math.sin(a - 0.5)); ctx.lineTo(x, y); ctx.lineTo(x - 8 * Math.cos(a + 0.5), y - 8 * Math.sin(a + 0.5)); ctx.stroke();
    if (d.lane === 0) { ctx.fillStyle = '#8c4ccc'; label(d.name, x + 8, y + 8, 'task', d.taskId); }
  }
  for (const c of installationEstimate(S).cables) {
    if (!c.valid) continue;
    ctx.strokeStyle = '#308fcb'; ctx.lineWidth = 2; ctx.setLineDash([7, 4]);
    ctx.beginPath(); c.points.forEach((p, i) => i ? ctx.lineTo(...point(p)) : ctx.moveTo(...point(p))); ctx.stroke();
  }
  if (draft?.kind === 'cable') {
    ctx.strokeStyle = '#308fcb'; ctx.lineWidth = 2;
    const cam = S.cams.find(c => c.id === draft.camId), device = S.equipment.find(e => e.id === draft.equipmentId);
    const pts = [cam, ...draft.pts, hover, device].filter(Boolean);
    ctx.beginPath(); pts.forEach((p, i) => i ? ctx.lineTo(...point(p)) : ctx.moveTo(...point(p))); ctx.stroke();
  }
  ctx.setLineDash([]);
  for (const light of S.lights || []) {
    const pose = lightPose(S, light), [x, y] = point(pose);
    const selectedLight=selected?.type==='light'&&selected.id===light.id;
    if((view.k<25 || labelMode==='essential') && !selectedLight) {
      const d=S.lighting?.definitions?.find(d=>d.id===light.definitionId),r=Math.max(2,Math.min(5,view.k*(d?.fixtureType==='panel'?.3:.14)));
      ctx.save();ctx.strokeStyle='#b39748';ctx.fillStyle=pose.enabled?'#f6e8ba':'#cbd5e1';ctx.lineWidth=1;
      if(d?.fixtureType==='panel'){ctx.fillRect(x-r,y-r,r*2,r*2);ctx.strokeRect(x-r,y-r,r*2,r*2);}else{ctx.beginPath();ctx.arc(x,y,r,0,Math.PI*2);ctx.fill();ctx.stroke();}ctx.restore();continue;
    }
    ctx.save(); ctx.strokeStyle = '#9a650d'; ctx.fillStyle = pose.enabled ? '#ffe8a0' : '#cbd5e1'; ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
    for (let i = 0; i < 8; i++) { const a = i * Math.PI / 4; ctx.beginPath(); ctx.moveTo(x + Math.cos(a) * 9, y + Math.sin(a) * 9); ctx.lineTo(x + Math.cos(a) * 12, y + Math.sin(a) * 12); ctx.stroke(); }
    ctx.fillStyle = '#99640d'; label(light.name, x + 15, y - 10, 'light', light.id); ctx.restore();
  }
  for (const e of S.equipment || []) {
    const [x, y] = point(e); ctx.fillStyle = '#237aad'; ctx.fillRect(x - 7, y - 7, 14, 14); label(e.name, x + 10, y - 9, 'equipment', e.id);
  }
  for (const target of S.targets || []) {
    const [x, y] = point(target);
    ctx.save(); ctx.translate(x, y); ctx.rotate((target.pan || 0) * Math.PI / 180);
    if (target.kind === 'vehicle') { ctx.fillStyle = '#466f94'; ctx.fillRect(-2.2 * view.k, -0.9 * view.k, 4.4 * view.k, 1.8 * view.k); }
    else for (const p of personParts(S, target).filter(p => !['face', 'eye', 'neck'].includes(p.role)).sort((a, b) => a.z - b.z)) {
      ctx.fillStyle = p.color; ctx.beginPath(); ctx.ellipse(p.x * view.k, p.y * view.k, Math.max(2, p.width * view.k / 2), Math.max(2, p.depth * view.k / 2), 0, 0, Math.PI * 2); ctx.fill();
    }
    ctx.strokeStyle = '#9856d6'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(10, 0); ctx.lineTo(25, 0); ctx.lineTo(20, -4); ctx.moveTo(25, 0); ctx.lineTo(20, 4); ctx.stroke();
    ctx.restore(); ctx.fillStyle = '#7950a0'; label(target.name, x + 9, y + 22, 'target', target.id);
  }
  for (const o of S.obstacles || []) {
    if (!presentInScene(o, S.sceneState)) continue;
    if (['tree','plant'].includes(o.kind)) {
      const [x,y]=point(o);ctx.save();ctx.translate(x,y);ctx.rotate((o.pan||0)*Math.PI/180);
      ctx.fillStyle=o.kind==='tree'?'#648452aa':'#72945dc9';ctx.strokeStyle='#4f6c42';ctx.lineWidth=1;
      ctx.beginPath();ctx.ellipse(0,0,o.width*view.k/2,o.depth*view.k/2,0,0,Math.PI*2);ctx.fill();ctx.stroke();
      ctx.fillStyle='#796a51';ctx.beginPath();ctx.arc(0,0,Math.max(1.5,o.width*view.k*.045),0,Math.PI*2);ctx.fill();ctx.restore();
    }
    if (isPoleObject(o)) {
      const [x, y] = point(o);
      ctx.fillStyle = o.kind === 'light-pole' ? '#edf0f3' : '#bfaa89'; ctx.strokeStyle = '#465663'; ctx.lineWidth = 1.5;
      ctx.beginPath(); ctx.arc(x, y, Math.max(5, poleDiameter(o) * view.k / 2), 0, Math.PI * 2); ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(x - 3, y); ctx.lineTo(x + 3, y); ctx.moveTo(x, y - 3); ctx.lineTo(x, y + 3); ctx.stroke();
    }
    const [x, y] = point(o); ctx.fillStyle = '#64748b'; label(o.name, x + 9, y + o.depth * view.k / 2 + 13, 'obstacle', o.id);
  }
  const selectedObject = selected?.type === 'light' ? lightPose(S, S.lights.find(l => l.id === selected.id) || {}) : selected?.type === 'stair' ? S.stairs.find(o => o.id === selected.id) : selected?.type === 'target' ? S.targets.find(o => o.id === selected.id) : selected?.type === 'obstacle' ? S.obstacles.find(o => o.id === selected.id) : selected?.type === 'task' ? S.tasks.find(o => o.id === selected.id && !o.openingId) : null;
  if (selectedObject && (selected.type !== 'obstacle' || presentInScene(selectedObject, S.sceneState))) {
    const [x, y] = point(selectedObject), a = (selectedObject.heading ?? selectedObject.pan ?? 0) * Math.PI / 180;
    ctx.strokeStyle = '#2563eb'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(x, y, 14, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * 34, y + Math.sin(a) * 34); ctx.stroke();
    ctx.fillStyle = '#f59e0b'; ctx.beginPath(); ctx.arc(x + Math.cos(a) * 34, y + Math.sin(a) * 34, 5, 0, Math.PI * 2); ctx.fill();
  }
  for (const cam of S.cams.filter(c => c.locked)) {
    const [x, y] = point(cam); ctx.strokeStyle = '#d99516'; ctx.lineWidth = 2; ctx.strokeRect(x - 11, y - 11, 22, 22);
  }
  if (probe) {
    const report = explainPoint(S, probe.x, probe.y, probe.req, { heading: probe.heading });
    const rows = selected?.type === 'cam' ? report.rows.filter(r => r.cam.id === selected.id) : report.rows.slice(0, 6);
    for (const r of rows) {
      ctx.strokeStyle = r.met ? '#139b76' : r.obstruction && r.inFrame ? '#d65a53' : '#ad8740'; ctx.lineWidth = 1.5; ctx.setLineDash([5, 3]);
      ctx.beginPath(); ctx.moveTo(...point(r.cam)); ctx.lineTo(...point(probe)); ctx.stroke();
      if (r.hit && r.inFrame) { const [x, y] = point(r.hit); ctx.setLineDash([]); ctx.beginPath(); ctx.moveTo(x - 5, y - 5); ctx.lineTo(x + 5, y + 5); ctx.moveTo(x - 5, y + 5); ctx.lineTo(x + 5, y - 5); ctx.stroke(); }
    }
    const [x, y] = point(probe); ctx.setLineDash([]); ctx.strokeStyle = '#8c4ccc'; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, 8, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.restore();
}
